/**
 * Authentication Service
 * Handles all authentication-related API calls to the backend
 */

function resolveApiBase() {
  let base = window.BATEMO_API?.baseUrl !== undefined ? window.BATEMO_API.baseUrl : '/api/v1';
  if (window.location.protocol === 'https:' && typeof base === 'string' && base.startsWith('http://')) {
    base = base.replace('http://', 'https://');
  }
  return base;
}

const API_BASE = {
  toString: () => resolveApiBase(),
  valueOf: () => resolveApiBase()
};

// NOTE: deliberate small duplicate of `ApiShapeError` / `assertJsonResponse`
// from `modules/dataService.js`. dataService.js imports this module statically
// (`getAccessToken`, `isDevelopment`), so importing it back here would create a
// module cycle. Keep the two copies in sync.

/**
 * Thrown when an HTTP response is "successful" but is not JSON — almost always
 * the SPA's own index.html served by the deploy fallback.
 */
export class ApiShapeError extends Error {
  constructor(message, { url, contentType } = {}) {
    super(message);
    this.name = 'ApiShapeError';
    this.code = 'API_SHAPE';
    this.url = url;
    this.contentType = contentType;
  }
}

/**
 * Guard a response before `await res.json()`.
 *
 * Every `frontend:build:*` ships an `.htaccess` whose catch-all rewrite sends
 * unknown paths to `index.html`, so a missing or renamed API route answers
 * 200 text/html with the SPA shell — `res.ok` does not prove the body is JSON.
 * A response with no content-type at all is tolerated (the fallback always
 * ships `text/html`).
 *
 * @throws {ApiShapeError} when a content-type is present and is not JSON
 */
export function assertJsonResponse(res, context) {
  const contentType = res?.headers?.get?.('content-type') || '';
  if (!contentType) return;
  if (contentType.toLowerCase().includes('application/json')) return;

  const where = context || res?.url || 'the API';
  throw new ApiShapeError(
    `Expected JSON from ${where} but got "${contentType}" — the response looks like the SPA index.html fallback (missing or renamed API route).`,
    { url: where, contentType }
  );
}

/**
 * Check if running in development mode (localhost or local network)
 */
export function isDevelopment() {
  const hostname = window.location.hostname;
  return hostname === 'localhost' ||
         hostname === '127.0.0.1' ||
         hostname.startsWith('192.168.') ||
         hostname.startsWith('10.') ||
         hostname.startsWith('172.');
}

/**
 * Get authentication headers (dev token or JWT)
 */
async function getAuthHeaders() {
  const headers = {};

  if (isDevelopment()) {
    // Development: Check if user has JWT token from manual login
    const jwtToken = localStorage.getItem('access_token');

    if (jwtToken) {
      // Use JWT Bearer token for authentication
      headers['Authorization'] = `Bearer ${jwtToken}`;
    }
  } else {
    // Production: Use JWT Bearer token
    const token = localStorage.getItem('access_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
}

// ─── Ion quota header interceptor ────────────────────────────────────────
// Any backend response carrying X-Quota-* headers updates the central chat
// quota signal. Lazy-loaded so non-chat pages (login, register, public)
// don't pay the chatStore import cost at boot.
let _chatStoreModulePromise = null
function loadChatStore() {
  if (!_chatStoreModulePromise) {
    _chatStoreModulePromise = import('../state/chatStore.js')
  }
  return _chatStoreModulePromise
}

function parseIntOrNull(raw) {
  if (raw == null) return null
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : null
}

function maybeUpdateQuotaFromHeaders(response) {
  if (!response || !response.headers) return
  const h = response.headers

  // Preferred: dual-bucket headers (`X-Chat-Daily-*` / `X-Chat-Weekly-*`)
  const dailyPct = parseIntOrNull(h.get('X-Chat-Daily-Used-Pct'))
  const weeklyPct = parseIntOrNull(h.get('X-Chat-Weekly-Used-Pct'))
  const dailyResetAt = h.get('X-Chat-Daily-Reset-At')
  const weeklyResetAt = h.get('X-Chat-Weekly-Reset-At')
  const model = h.get('X-Chat-Model')
  const conversationId = h.get('X-Conversation-Id')

  const hasDualBucket =
    dailyPct != null || weeklyPct != null || dailyResetAt || weeklyResetAt

  // Legacy: `X-Quota-*` (single-bucket). Kept as fallback for endpoints that
  // haven't been retrofitted yet.
  const legacyUsed = parseIntOrNull(h.get('X-Quota-Used'))
  const legacyLimit = parseIntOrNull(h.get('X-Quota-Limit'))
  const legacyReset = h.get('X-Quota-Reset-At')

  const hasLegacy =
    legacyUsed != null || legacyLimit != null || legacyReset

  if (!hasDualBucket && !hasLegacy && !model && !conversationId) return

  loadChatStore()
    .then((m) => {
      if (hasDualBucket) {
        m.updateQuota({
          daily:
            dailyPct != null || dailyResetAt
              ? { pct: dailyPct, resetAt: dailyResetAt }
              : undefined,
          weekly:
            weeklyPct != null || weeklyResetAt
              ? { pct: weeklyPct, resetAt: weeklyResetAt }
              : undefined,
        })
      } else if (hasLegacy) {
        m.updateQuota({ used: legacyUsed, limit: legacyLimit, resetAt: legacyReset })
      }
      if (model && typeof m.setLastUsedModel === 'function') {
        m.setLastUsedModel(model)
      }
      if (conversationId && typeof m.setCurrentConversationId === 'function') {
        m.setCurrentConversationId(conversationId)
      }
    })
    .catch((err) => console.warn('[authService] quota interceptor failed:', err))
}

/**
 * Fetch with authentication headers (dev token or JWT)
 * Automatically handles token refresh in production mode.
 *
 * Side effect: reads X-Quota-Used/Limit/Reset-At response headers (if any)
 * and pipes them into the Ion chat quota signal. See `maybeUpdateQuotaFromHeaders`.
 */
export async function authenticatedFetch(url, options = {}) {
  const authHeaders = await getAuthHeaders();
  const headers = {
    ...authHeaders,
    ...(options.headers || {})
  };

  let response = await fetch(url, {
    ...options,
    headers
  });

  // On 401, try the refresh-token rotation. Dev and prod use the same JWT
  // path now (the old "dev token" mode no longer exists), so refresh runs
  // in both — a 30-min access token shouldn't force a re-login when a
  // 7-day refresh token is sitting in localStorage.
  if (response.status === 401) {
    try {
      await refreshAccessToken();
      const newAuthHeaders = await getAuthHeaders();
      const newHeaders = {
        ...newAuthHeaders,
        ...(options.headers || {})
      };
      response = await fetch(url, {
        ...options,
        headers: newHeaders
      });
    } catch (e) {
      logout();
      throw new Error('Session expired');
    }
  }

  maybeUpdateQuotaFromHeaders(response);

  return response;
}

/**
 * Register a new user
 */
export async function registerUser(userData) {
  const apiBase = resolveApiBase();
  const response = await fetch(`${apiBase}/users/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: userData.email,
      password: userData.password,
      title: userData.title || null,
      first_name: userData.firstName,
      last_name: userData.lastName,
      company: userData.company,  // Required field
      full_name: userData.fullName || `${userData.firstName} ${userData.lastName}`,
      marketing_email_consent: userData.marketingConsent != null ? userData.marketingConsent : undefined
    })
  });

  if (!response.ok) {
    let errorData;
    // Clone BEFORE parsing: `.json()` consumes the body, so reading `.text()`
    // afterwards threw "body stream already read" and masked the real status.
    const errorBodyClone = typeof response.clone === 'function' ? response.clone() : null;
    try {
      errorData = await response.json();
    } catch (e) {
      // Response is not JSON (e.g., HTML error page)
      try {
        const text = errorBodyClone ? await errorBodyClone.text() : '';
        console.warn('[Auth] Non-JSON error response:', text.substring(0, 200));
      } catch (readErr) {
        console.warn('[Auth] Non-JSON error response (body unreadable):', readErr);
      }
      throw new Error(`Registration failed (${response.status})`);
    }

    // Throw the error data directly so the caller can handle it
    throw errorData;
  }

  assertJsonResponse(response, `${apiBase}/users/register`);
  return response.json();
}

/**
 * Login with email and password
 */
export async function login(email, password) {
  const apiBase = resolveApiBase();

  const response = await fetch(`${apiBase}/users/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });

  if (!response.ok) {
    // Check if response is JSON or HTML
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    } else {
      // Got HTML instead of JSON - API endpoint issue
      const text = await response.text();
      console.error('[Auth] Login returned HTML instead of JSON:', text.substring(0, 200));
      throw new Error(`API endpoint not found (${response.status})`);
    }
  }

  const data = await response.json();

  // CRITICAL: Clear any cached data from previous user before storing new tokens
  // This prevents data leakage when switching between users
  console.log('[Auth] Login successful - clearing previous user cache...');
  try {
    const { clearCache } = await import('../modules/dataService.js');
    await clearCache();
    console.log('[Auth] ✓ Previous user cache cleared');
  } catch (e) {
    console.warn('[Auth] Could not clear previous cache:', e);
  }

  // Same for Ion: a session that ended WITHOUT logout (expired token, closed
  // tab) can leave the previous account's conversation id in the store — which
  // was already imported by the time we get here, so the persisted-key
  // user-scoping alone wouldn't help. Starting every login on a fresh chat is
  // the safe default; the conversation itself is still in the history drawer.
  try {
    const ion = await import('../state/chatStore.js');
    ion.clearConversation();
    ion.clearIonPersistedState();
  } catch (e) {
    console.warn('[Auth] Could not clear previous Ion chat state:', e);
  }

  // Store tokens
  localStorage.setItem('access_token', data.access_token);
  localStorage.setItem('refresh_token', data.refresh_token);

  // Hydrate tour state from backend (fire-and-forget, don't block login UX)
  import('../tours/tourSyncService.js')
    .then(m => m.hydrateTourStateFromBackend())
    .catch(() => {})

  return data;
}

/**
 * Logout - clear local storage, user state, and ALL cached data
 * This is critical for security: prevents next user from seeing previous user's data
 */
export async function logout() {
  console.log('[Auth] Logging out - clearing all user data and cache...');

  // Stop the engagement heartbeat (best-effort, fire-and-forget)
  try {
    const { stopHeartbeat } = await import('./engagementService.js');
    stopHeartbeat();
  } catch (_err) {
    // ignore — engagement tracking is non-critical
  }

  // Clear auth tokens
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('current_user');

  // Clear user state signals
  import('../state/userState.js').then(({ clearUserState }) => {
    clearUserState();
  });

  // CRITICAL: clear Ion chat state — in-memory (messages, conversation id,
  // draft, pinned artifact, selection context) AND its persisted
  // 'batemo:ion:*' localStorage keys. Without this the next account in the
  // same browser inherits the previous one's conversation id, re-sends it to
  // POST /chat/stream, and sees the previous account's thread until the
  // server rejects the id.
  try {
    const ion = await import('../state/chatStore.js');
    ion.clearConversation();
    ion.clearIonPersistedState();
    console.log('[Auth] ✓ Ion chat state cleared');
  } catch (e) {
    console.warn('[Auth] Could not clear Ion chat state:', e);
  }

  // Clear the DEMO daily view-limit meter/mirror so the next user starts clean
  import('../state/demoLimitStore.js').then(({ reset }) => {
    reset();
  }).catch(() => { /* non-critical */ });

  // CRITICAL: Clear ALL cached cell data to prevent data leakage between users
  try {
    const { clearCache } = await import('../modules/dataService.js');
    await clearCache();
    console.log('[Auth] ✓ All cached data cleared');
  } catch (e) {
    console.warn('[Auth] Could not clear data cache:', e);
  }

  // Also clear any other app-specific localStorage items
  try {
    // Clear cell slug map (user-specific data)
    localStorage.removeItem('batemo_slug_map');
    localStorage.removeItem('batemo_slug_map_v');
    // Keep theme preference (not user-specific)
  } catch (e) {
    console.warn('[Auth] Could not clear additional localStorage:', e);
  }
}

/**
 * Refresh access token
 */
export async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refresh_token');

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const apiBase = resolveApiBase();
  const response = await fetch(`${apiBase}/users/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken })
  });

  if (!response.ok) {
    logout(); // Clear invalid tokens
    throw new Error('Token refresh failed');
  }

  const data = await response.json();

  // Update tokens
  localStorage.setItem('access_token', data.access_token);
  localStorage.setItem('refresh_token', data.refresh_token);

  return data;
}

/**
 * Get current user profile
 */
export async function getCurrentUser() {
  const token = localStorage.getItem('access_token');
  if (!token) {
    throw new Error('Not authenticated');
  }

  const apiBase = resolveApiBase();
  const response = await authenticatedFetch(`${apiBase}/users/me`);

  // authenticatedFetch already attempted refresh-on-401 above. If it still
  // returns 401, both tokens are dead — fall through to logout.
  if (response.status === 401) {
    logout();
    throw new Error('Session expired');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch user profile');
  }

  const user = await response.json();
  localStorage.setItem('current_user', JSON.stringify(user));

  return user;
}

/**
 * Update user profile
 */
export async function updateProfile(updates) {
  const apiBase = resolveApiBase();

  // Build update payload with snake_case keys (backend expects snake_case)
  const payload = {};
  if (updates.title !== undefined) payload.title = updates.title;
  if (updates.first_name !== undefined) payload.first_name = updates.first_name;
  if (updates.last_name !== undefined) payload.last_name = updates.last_name;
  if (updates.company !== undefined) payload.company = updates.company;
  if (updates.full_name !== undefined) payload.full_name = updates.full_name;
  if (updates.email !== undefined) payload.email = updates.email;

  const response = await authenticatedFetch(`${apiBase}/users/me`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Profile update failed');
  }

  return response.json();
}

/**
 * Change password
 */
export async function changePassword(oldPassword, newPassword) {
  const token = localStorage.getItem('access_token');
  const apiBase = resolveApiBase();

  const response = await fetch(`${apiBase}/users/me/change-password`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      old_password: oldPassword,
      new_password: newPassword
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Password change failed');
  }
}

/**
 * Request password reset
 */
export async function requestPasswordReset(email) {
  const apiBase = resolveApiBase();
  const response = await fetch(`${apiBase}/users/request-password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });

  // Always returns success to prevent email enumeration
  if (!response.ok) {
    throw new Error('Request failed');
  }
}

/**
 * Resend verification email
 * Requires authenticated user with unverified email
 */
export async function resendVerificationEmail() {
  const apiBase = resolveApiBase();
  const response = await authenticatedFetch(`${apiBase}/users/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to resend verification email');
  }

  // 204 No Content - success
  return { success: true };
}

/**
 * Reset password with token
 */
export async function resetPassword(token, newPassword) {
  const apiBase = resolveApiBase();
  const response = await fetch(`${apiBase}/users/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token,
      new_password: newPassword
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Password reset failed');
  }
}

/**
 * Delete own account (GDPR compliance)
 * Cancels any active Stripe subscription and permanently deletes the account
 */
export async function deleteAccount() {
  const apiBase = resolveApiBase();
  const response = await authenticatedFetch(`${apiBase}/users/me`, {
    method: 'DELETE'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to delete account');
  }

  // Clear all local data after successful deletion
  await logout();

  return { success: true };
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return !!localStorage.getItem('access_token');
}

/**
 * Get stored access token
 */
export function getAccessToken() {
  return localStorage.getItem('access_token');
}

// Note: authenticatedFetch is defined at the top of this file
